import { readdir, stat } from "node:fs/promises";
import path from "node:path";

import {
  PRIMEUI_PROJECT_DIR_NAME,
  PrimeUiProjectConfigError,
  readPrimeUiProjectConfig,
  resolvePrimeUiTargetProjectRoot,
} from "../lib/project-link-config.js";
import type { NestedPrimeUiCandidate } from "./index.js";

const SCAN_EXCLUDED_DIRS = new Set([
  ".git",
  "node_modules",
  ".next",
  "dist",
  "build",
  "coverage",
]);

async function isFile(targetPath: string): Promise<boolean> {
  try {
    const targetStat = await stat(targetPath);
    return targetStat.isFile();
  } catch {
    return false;
  }
}

function mapConfigStatus(
  error: unknown,
): NestedPrimeUiCandidate["configStatus"] {
  if (!(error instanceof PrimeUiProjectConfigError)) {
    return "read-failed";
  }

  switch (error.code) {
    case "PROJECT_CONFIG_INVALID_JSON":
      return "invalid-json";
    case "PROJECT_CONFIG_INVALID_FORMAT":
      return "invalid-format";
    case "PROJECT_CONFIG_READ_FAILED":
      return "read-failed";
    case "PROJECT_CONFIG_NOT_FOUND":
      return "missing";
    default:
      return "read-failed";
  }
}

async function inspectCandidate(
  projectRoot: string,
  primeUiPath: string,
): Promise<NestedPrimeUiCandidate> {
  const configPath = path.join(primeUiPath, "project.json");

  if (!(await isFile(configPath))) {
    return {
      projectRoot,
      primeUiPath,
      configPath,
      hasProjectConfig: false,
      configStatus: "missing",
    };
  }

  try {
    const projectConfig = await readPrimeUiProjectConfig(configPath);
    return {
      projectRoot,
      primeUiPath,
      configPath,
      hasProjectConfig: true,
      configStatus: "valid",
      targetProjectPath: projectConfig.targetProjectPath,
      targetProjectRoot: resolvePrimeUiTargetProjectRoot(
        projectRoot,
        projectConfig,
      ),
    };
  } catch (error) {
    return {
      projectRoot,
      primeUiPath,
      configPath,
      hasProjectConfig: true,
      configStatus: mapConfigStatus(error),
    };
  }
}

export async function findNestedPrimeUiCandidates(
  rootPath: string,
): Promise<NestedPrimeUiCandidate[]> {
  const queue: string[] = [path.resolve(rootPath)];
  const seen = new Set<string>();
  const candidates: NestedPrimeUiCandidate[] = [];

  while (queue.length > 0) {
    const currentDir = queue.shift();
    if (!currentDir || seen.has(currentDir)) {
      continue;
    }
    seen.add(currentDir);

    let entries;
    try {
      entries = await readdir(currentDir, { withFileTypes: true });
    } catch {
      continue;
    }

    for (const entry of entries) {
      if (!entry.isDirectory()) {
        continue;
      }

      const entryPath = path.join(currentDir, entry.name);
      if (entry.name === PRIMEUI_PROJECT_DIR_NAME) {
        candidates.push(await inspectCandidate(currentDir, entryPath));
        continue;
      }

      if (SCAN_EXCLUDED_DIRS.has(entry.name)) {
        continue;
      }

      queue.push(entryPath);
    }
  }

  return candidates.sort((left, right) =>
    left.primeUiPath.localeCompare(right.primeUiPath),
  );
}
